// Pure helper: builds the field_jobs UPDATE payload for a status transition.
//
// Single source of truth for "what columns move when a job's status changes,"
// used by every call site that advances a job's status. Replaces the previous
// inline payload construction that lived in JobDrawer.handleStatusAdvance and
// JobDrawer.handleSendEnRoute, plus the Board card's next-status button which
// was missing the actual_start / actual_end timestamp side-effects entirely.
//
// Called from three sites today:
//   - JobDrawer.handleStatusAdvance  (FieldHub.tsx)
//   - JobDrawer.handleSendEnRoute    (FieldHub.tsx)
//   - Board card "→ Next" button     (FieldHub.tsx)
//
// `currentStatus` is accepted for caller readability — `(job.status, next)`
// reads as a natural transition expression — and reserves room for future
// per-source-state semantics. Today only `nextStatus` drives the payload.

export type FieldJobStatus =
  | 'scheduled'
  | 'en_route'
  | 'on_site'
  | 'complete'
  | 'cancelled'
  | 'on_customer_hold'
  | 'on_vendor_hold'
  | 'pending_parts'
  | 'awaiting_approval'

export type StatusTransitionPayload = {
  status: FieldJobStatus
  actual_start?: string
  actual_end?: string
}

export const NEXT_STATUS: Record<string, FieldJobStatus> = {
  scheduled: 'en_route',
  en_route:  'on_site',
  on_site:   'complete',
}

// Non-terminal pauses: work cannot advance via NEXT_STATUS while in any of
// these. Live timestamps (actual_start, actual_end) are NOT stamped when a
// job enters or exits a hold — see buildStatusTransitionPayload below.
export const HOLD_STATUSES: FieldJobStatus[] = [
  'on_customer_hold',
  'on_vendor_hold',
  'pending_parts',
  'awaiting_approval',
]

// Terminal statuses — nothing follows. Both NEXT_STATUS and canEnterHold
// treat these as dead-ends.
export const TERMINAL_STATUSES: FieldJobStatus[] = ['complete', 'cancelled']

// Any non-terminal status may enter a hold. A held job can re-enter a
// different hold (e.g. customer_hold → pending_parts) without resuming first.
export function canEnterHold(status: string): boolean {
  return !TERMINAL_STATUSES.includes(status as FieldJobStatus)
}

// Option C: holds resolve back to 'scheduled' regardless of where they were
// entered from. The UI will offer a manual override (resume-as) later.
export function resumeTarget(): FieldJobStatus {
  return 'scheduled'
}

// Holds pause the live footprint: entering/leaving a hold MUST NOT touch
// actual_start or actual_end. Only the forward chain stamps timestamps
// (en_route → actual_start, complete → actual_end).
export function buildStatusTransitionPayload(
  _currentStatus: string,
  nextStatus: FieldJobStatus,
  now: Date = new Date(),
): StatusTransitionPayload {
  const payload: StatusTransitionPayload = { status: nextStatus }
  if (nextStatus === 'en_route') payload.actual_start = now.toISOString()
  if (nextStatus === 'complete') payload.actual_end = now.toISOString()
  return payload
}
